import { useId } from 'react';

/**
 * Brand line pattern: concentric-square blocks (2×2 cells, with a split center
 * line) set in a checkerboard against blocks of alternating horizontal /
 * vertical hatched cells. All geometry is expressed in `unit`s — the spacing
 * between adjacent parallel lines. One cell is 10 units; one square block is
 * 20; the repeating tile is 40 × 40.
 *
 * Not a registered component (no contentSchema, never in registry.ts), so the
 * schema linter skips it.
 */

const CELL = 10;
const BLOCK = 20;
const TILE = 40;
const SQUARE_INSETS = 5;
// Half-length of the gap in each square block's center line.
const CENTER_GAP = 3.4;

type Segment = [x1: number, y1: number, x2: number, y2: number];

function tileSegments(): Segment[] {
  const segs: Segment[] = [];

  const square = (ox: number, oy: number) => {
    for (let k = 0; k <= SQUARE_INSETS; k++) {
      const a = k;
      const b = BLOCK - k;
      segs.push([ox + a, oy + a, ox + b, oy + a]);
      segs.push([ox + b, oy + a, ox + b, oy + b]);
      segs.push([ox + b, oy + b, ox + a, oy + b]);
      segs.push([ox + a, oy + b, ox + a, oy + a]);
    }
    const cx = ox + BLOCK / 2;
    segs.push([cx, oy, cx, oy + BLOCK / 2 - CENTER_GAP]);
    segs.push([cx, oy + BLOCK / 2 + CENTER_GAP, cx, oy + BLOCK]);
  };

  const hatch = (ox: number, oy: number, horizontal: boolean) => {
    for (let i = 0; i <= CELL; i++) {
      segs.push(
        horizontal ? [ox, oy + i, ox + CELL, oy + i] : [ox + i, oy, ox + i, oy + CELL],
      );
    }
  };

  // The tile's own elements, plus copies from the 8 neighbouring tiles so
  // strokes that straddle a tile edge are drawn on both sides of the seam.
  for (const dx of [-TILE, 0, TILE]) {
    for (const dy of [-TILE, 0, TILE]) {
      square(dx, dy);
      square(dx + BLOCK, dy + BLOCK);
      hatch(dx + 20, dy + 0, true);
      hatch(dx + 30, dy + 0, false);
      hatch(dx + 20, dy + 10, false);
      hatch(dx + 30, dy + 10, true);
      hatch(dx + 0, dy + 20, false);
      hatch(dx + 10, dy + 20, true);
      hatch(dx + 0, dy + 30, true);
      hatch(dx + 10, dy + 30, false);
    }
  }

  return segs.filter(
    ([x1, y1, x2, y2]) =>
      Math.max(x1, x2) >= 0 && Math.min(x1, x2) <= TILE && Math.max(y1, y2) >= 0 && Math.min(y1, y2) <= TILE,
  );
}

const SEGMENTS = tileSegments();

const r = (n: number) => Math.round(n * 100) / 100;

/** Path data for one tile at the given unit size (tile is `40 * unit` square). */
export function brandPatternPath(unit: number): string {
  return SEGMENTS.map(
    ([x1, y1, x2, y2]) => `M${r(x1 * unit)} ${r(y1 * unit)}L${r(x2 * unit)} ${r(y2 * unit)}`,
  ).join('');
}

export function brandPatternTileSize(unit: number): number {
  return TILE * unit;
}

/**
 * Data-URI tile for CSS `background-image`. CSS variables don't resolve inside
 * data URIs, so `color` must be a concrete color value.
 */
export function brandPatternDataUri(color: string, unit: number, strokeWidth = 1): string {
  const size = brandPatternTileSize(unit);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'><path d='${brandPatternPath(unit)}' fill='none' stroke='${color}' stroke-width='${strokeWidth}'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

function PatternSvg({
  patternId,
  height,
  unit,
  className,
}: {
  patternId: string;
  height: number;
  unit: number;
  className: string;
}) {
  const size = brandPatternTileSize(unit);
  return (
    <svg className={`block w-full ${className}`} width="100%" height={height} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id={patternId} x="0" y="0" width={size} height={size} patternUnits="userSpaceOnUse">
          <path d={brandPatternPath(unit)} fill="none" stroke="var(--vibe-color-secondary)" strokeWidth={1} />
        </pattern>
      </defs>
      <rect width="100%" height={height} fill={`url(#${patternId})`} />
    </svg>
  );
}

/** Line spacing on desktop (≥ lg) and below lg. */
export const PATTERN_UNIT_DESKTOP = 10;
export const PATTERN_UNIT_MOBILE = 6;

/**
 * Decorative full-width band of the brand pattern, stroked in the secondary
 * theme color. `className` styles the wrapper (background, margins, z-index).
 *
 * `height` is the desktop (≥ lg) band height; tablet (md–lg) renders at half
 * height, and mobile (< md) at `mobileHeight` (defaults to half height).
 * Below lg the pattern is drawn at a smaller unit so it stays legible in the
 * shorter band.
 */
export function PatternBand({
  className = '',
  height = 100,
  mobileHeight,
}: {
  className?: string;
  height?: number;
  mobileHeight?: number;
}) {
  // useId() can contain ":" which is awkward inside url(#…); strip it.
  const base = `brand-pattern-${useId().replace(/:/g, '')}`;
  return (
    <div className={`leading-[0] ${className}`} aria-hidden="true">
      <PatternSvg patternId={`${base}-d`} height={height} unit={PATTERN_UNIT_DESKTOP} className="hidden lg:block" />
      <PatternSvg
        patternId={`${base}-t`}
        height={height / 2}
        unit={PATTERN_UNIT_MOBILE}
        className="hidden md:block lg:hidden"
      />
      <PatternSvg
        patternId={`${base}-m`}
        height={mobileHeight ?? height / 2}
        unit={PATTERN_UNIT_MOBILE}
        className="md:hidden"
      />
    </div>
  );
}
